import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, KeyRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { authApi } from '../../services/api';
import { PasswordStrength, evaluatePassword } from './PasswordStrength';

/**
 * ChangePasswordCard — single-purpose form on the User Profile page.
 * On success the server returns a fresh JWT (token_generation was bumped); we
 * swap it into localStorage so the current session stays valid.
 */
const ChangePasswordCard = () => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const strengthMet = evaluatePassword(next).allMet;
  const matches = confirm.length > 0 && next === confirm;
  const different = next.length > 0 && next !== current;
  const allOk = strengthMet && matches && different;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!current) {
      toast.error('Enter your current password.');
      return;
    }
    if (!allOk) {
      toast.error('Please choose a new password that meets all the requirements.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await authApi.changePassword(current, next);
      // Server returns a freshly-issued token with the bumped token_generation
      if (data?.token) localStorage.setItem('equipr_token', data.token);
      toast.success('Password updated.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Could not change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="change-password-card">
      <CardHeader>
        <CardTitle className="uppercase tracking-wide flex items-center gap-2">
          <KeyRound className="w-5 h-5" />
          Change password
        </CardTitle>
        <CardDescription>Update the password you use to sign in.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="cp-current" className="uppercase text-xs tracking-wider">Current password</Label>
            <Input
              id="cp-current"
              type={show ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              data-testid="cp-current-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cp-new" className="uppercase text-xs tracking-wider">New password</Label>
            <div className="relative">
              <Input
                id="cp-new"
                type={show ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                className="pr-12"
                required
                data-testid="cp-new-input"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cp-confirm" className="uppercase text-xs tracking-wider">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              data-testid="cp-confirm-input"
            />
          </div>

          <PasswordStrength
            password={next}
            testId="cp-strength"
            extraRules={[
              { label: 'Both passwords match', passed: matches },
              { label: 'Different from current password', passed: different },
            ]}
          />

          <Button
            type="submit"
            disabled={loading || !allOk}
            className="btn-volt"
            data-testid="cp-submit-btn"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {loading ? 'Updating...' : 'Update password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordCard;
